/**
 * Declarative config for the onboarding flow.
 *
 * Several related questions share a screen under one header: only the first is
 * visible to begin with, answering it reveals the next beneath it, and a single
 * Continue moves to the next group. The orchestrator (index.tsx) walks this
 * list; step components call next() with zero knowledge of ordering, so the
 * flow is reshaped by editing this file only.
 */

export type StepKey =
  | 'name'
  | 'goal'
  | 'experience'
  | 'exam'
  | 'writing'
  | 'placement'
  | 'gate'
  | 'result'
  | 'attribution'
  | 'trial'

export interface FlowContext {
  experience: '' | 'yes' | 'no'
  hasWritingPreferences: boolean
  isAuthed: boolean
  /** /learn-chinese/existing — an authed user re-running the placement test */
  isExistingFlow: boolean
}

export type GroupKey = 'identity' | 'customize' | 'level' | 'result' | 'trial'

export interface GroupedStep {
  key: StepKey
  isApplicable: (ctx: FlowContext) => boolean
}

export interface FlowGroup {
  key: GroupKey
  /**
   * The screen's h1, as a translation key (index.tsx renders t(header)). Null
   * marks a pass-through group: a single full-page step (result, trial) that
   * owns its own heading and advance control.
   */
  header: string | null
  /** Optional line under the h1, saying what the answers are used for. A translation key. */
  subheader?: string
  steps: GroupedStep[]
  showProgress: boolean
}

export const FLOW_GROUPS: FlowGroup[] = [
  {
    key: 'identity',
    // The email is the first thing asked and the header is about the email:
    // "let us get to know you better" over a name box read as a registration
    // wall, and the first question is where the flow used to lose people.
    header: 'onboarding:groups.identity.header',
    subheader: 'onboarding:groups.identity.subheader',
    steps: [
      { key: 'gate', isApplicable: (ctx) => !ctx.isAuthed },
      // Sits after the gate so nothing is typed before the email is in. It
      // seeds itself from the address, so answering it is a click, not typing.
      // Applicability is resolved against the auth state at group entry (see
      // entryAuthRef in index.tsx), so verifying the code mid-group does not
      // pull this question off the screen the user is looking at.
      { key: 'name', isApplicable: (ctx) => !ctx.isAuthed },
      { key: 'attribution', isApplicable: (ctx) => !ctx.isExistingFlow },
    ],
    showProgress: true,
  },
  {
    key: 'customize',
    header: 'onboarding:groups.customize.header',
    steps: [
      { key: 'goal', isApplicable: (ctx) => !ctx.isExistingFlow },
      // `experience` precedes `writing` here, unlike the requested question
      // order: the script question is only asked of people who have studied
      // before, so the answer that gates it has to already be in.
      { key: 'experience', isApplicable: () => true },
      { key: 'writing', isApplicable: (ctx) => ctx.experience === 'yes' && ctx.hasWritingPreferences },
    ],
    showProgress: true,
  },
  {
    key: 'level',
    header: 'onboarding:groups.level.header',
    steps: [
      { key: 'exam', isApplicable: (ctx) => ctx.experience === 'yes' },
      { key: 'placement', isApplicable: (ctx) => ctx.experience === 'yes' },
    ],
    showProgress: true,
  },
  { key: 'result', header: null, steps: [{ key: 'result', isApplicable: () => true }], showProgress: false },
  { key: 'trial', header: null, steps: [{ key: 'trial', isApplicable: () => true }], showProgress: false },
]

/** Groups the user has already finished auth for — never bounce to /stories from these. */
export const POST_GATE_GROUPS: GroupKey[] = ['customize', 'level', 'result', 'trial']

/** Reaching these means the flow is done being a questionnaire; Back is closed off. */
const NO_BACK_GROUPS: GroupKey[] = ['result', 'trial']

export const groupSteps = (group: FlowGroup, ctx: FlowContext): StepKey[] =>
  group.steps.filter((s) => s.isApplicable(ctx)).map((s) => s.key)

/** A group with no applicable questions left is skipped entirely. */
export const applicableGroups = (ctx: FlowContext): FlowGroup[] =>
  FLOW_GROUPS.filter((g) => groupSteps(g, ctx).length > 0)

export const firstGroup = (ctx: FlowContext): GroupKey => applicableGroups(ctx)[0].key

export const findGroup = (key: GroupKey): FlowGroup | undefined => FLOW_GROUPS.find((g) => g.key === key)

export const nextGroupKey = (current: GroupKey, ctx: FlowContext): GroupKey | null => {
  const groups = applicableGroups(ctx)
  const idx = groups.findIndex((g) => g.key === current)
  // The current group may have emptied out under us (auth landing removes the
  // name + email questions) — fall back to the first applicable group after its
  // position in the master list.
  if (idx === -1) {
    const masterIdx = FLOW_GROUPS.findIndex((g) => g.key === current)
    const following = FLOW_GROUPS.slice(masterIdx + 1).find((g) => groupSteps(g, ctx).length > 0)
    return following ? following.key : null
  }
  return groups[idx + 1]?.key ?? null
}

export const prevGroupKey = (current: GroupKey, ctx: FlowContext): GroupKey | null => {
  if (NO_BACK_GROUPS.includes(current)) return null
  const groups = applicableGroups(ctx)
  const idx = groups.findIndex((g) => g.key === current)
  if (idx <= 0) return null
  return groups[idx - 1].key
}

export const groupProgress = (current: GroupKey, ctx: FlowContext): { index: number; total: number } | null => {
  // Until the experience branch is answered, assume the longest path so the
  // total can only shrink as the flow resolves — a growing total mid-flow
  // feels like moving backwards.
  const optimisticCtx: FlowContext =
    ctx.experience === '' ? { ...ctx, experience: 'yes', hasWritingPreferences: true } : ctx
  const groups = applicableGroups(optimisticCtx).filter((g) => g.showProgress)
  const idx = groups.findIndex((g) => g.key === current)
  if (idx === -1) return null
  return { index: idx + 1, total: groups.length }
}
